import * as React from 'react';

import {Label} from 'shells/form/label';
import {TextField} from 'shells/textfield/textfield';
import {ESC} from 'shells/utils/keys';

import type {EntityAttribute as EntityAttributeFragment} from 'nutshell-graphql-types';

import {InlineFormWrapper} from './inline-form-wrapper';

type Props = {
    field: EntityAttributeFragment;
    value: string | null | undefined;
    onChange: (newVal: string) => void;
    onSubmit: () => any;
    onCancel: () => any;
    blinkSuccess: boolean;
    isDirty: boolean;
    label: string;
    multiline?: boolean;
};

type State = {
    isFocused: boolean;
};

/*
 * Base textfield class responsible for rendering a react
 * "Edit In Place" textfield component, that supports…
 *
 * - Enter-to-submit
 * - Blur-to-submit
 * - Submit/Cancel buttons when focused
 * - Esc-to-cancel
 * - Flashing green text when recently submitted
 */
export class InlineTextfieldForm extends React.PureComponent<Props, State> {
    textField: HTMLInputElement | null | undefined;

    static defaultProps = {
        multiline: false,
    };

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isFocused: false,
        };
    }

    render() {
        return (
            <InlineFormWrapper
                shouldShowControls={this.state.isFocused}
                canSubmit={this.props.isDirty}
                onSubmit={this.props.onSubmit}
                onCancel={this.handleCancel}
            >
                <Label htmlFor={this.props.field.attribute.name}>{this.props.label}</Label>
                <TextField
                    textFieldRef={(n) => {
                        this.textField = n;
                    }}
                    // @ts-expect-error
                    name={this.props.field.attribute.name}
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    onEnter={!this.props.multiline ? this.handleEnter : undefined}
                    onKeyDown={this.handleKeyDown}
                    selectOnFocus={true}
                    blinkSuccess={this.props.blinkSuccess}
                    onChange={this.handleChange}
                    placeholder={`Add ${this.props.field.attribute.name}…`}
                    multiline={this.props.multiline}
                    value={this.props.value || ''}
                />
            </InlineFormWrapper>
        );
    }

    handleChange = (newVal: string) => {
        this.props.onChange(newVal);
    };

    handleEnter = () => {
        this.programaticallyBlurInput();
        this.props.onSubmit();
    };

    handleCancel = () => {
        this.programaticallyBlurInput();
        this.props.onCancel();
    };

    handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.keyCode === ESC) {
            this.handleCancel();
        }
    };

    handleFocus = () => {
        this.setState({isFocused: true});
    };

    handleBlur = () => {
        // We don't want a blur event immediately submitting the input,
        // because that would negate our cancel button. Therefore,
        // we'll use a timeout here and manage blur state separately.
        setTimeout(() => {
            if (this.state.isFocused) {
                this.setState({isFocused: false});
                this.props.onSubmit();
            }
        }, 100);
    };

    programaticallyBlurInput = () => {
        if (this.textField) {
            this.setState({isFocused: false}, () => {
                // Flow needs us to check this again, sadly.
                if (this.textField) {
                    this.textField.blur();
                }
            });
        }
    };
}
